import { useStore } from "@store/store";
import { useLocale } from "next-intl";
import { useParams, useSearchParams } from "next/navigation";
import { useProduct } from "@lib/commerce/hooks/productOperations";
import { useMemo } from "react";

export const useActiveVariant = () => {
    const { colorCode: paramsColorCode, articleNumber: paramsArticleNumber } = useParams();
    const activeProduct = useStore((state) => state.data.pdpState.activeProduct);
    const locale = useLocale();
    const sku = `${paramsArticleNumber}${activeProduct?.colorCode ?? paramsColorCode}`;
    const { product } = useProduct({ sku, locale });
    const searchParams = useSearchParams();
    const size = searchParams.get("size");

    const selectedVariant = useMemo(() => {
        if (!size) {
            return null;
        }

        return product?.variants?.find((variant) => {
            // Get current variant based on selected size
            return variant.attributesRaw.some(
                (attr) => attr.name === `Size` && attr.value.toString() === size
            );
        });
    }, [product?.variants, size]);

    const fallbackVariant = useMemo(() => {
        return product?.variants?.[product?.variants?.length - 1] ?? null;
    }, [product?.variants]);

    const activeVariant = selectedVariant ?? fallbackVariant;

    const colorCode = product?.colorCode ?? paramsColorCode;
    const articleNumber = product?.articleNumber ?? paramsArticleNumber;

    return {
        /**
         * The currently selected variant based on the user selection of size
         */
        selectedVariant,
        /**
         * The fallback variant when no size is selected
         */
        fallbackVariant,
        /**
         * The active variant to be used (selected by user or fallback).
         */
        activeVariant,
        colorCode,
        articleNumber,
    };
};
